import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import CityLivingCard from "../components/CityLivingCard";
import CurrencyConverter from "../components/CurrencyConverter";
import NewsletterForm from "../components/NewsletterForm";
import PolandMap from "../components/PolandMap";
import { fetchCities } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import {
  Home,
  Wallet,
  FileText,
  Briefcase,
  Users,
  HeartPulse,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import { connaissancesRubriques } from "../data/connaissances";
import { BLOG_POSTS } from "../data/blogPosts";

// Display order requested by the redesign brief.
const CITY_ORDER = [
  "varsovie",
  "cracovie",
  "wroclaw",
  "gdansk",
  "poznan",
  "lodz",
  "katowice",
];

function orderCities(cities) {
  const bySlug = new Map(cities.map((c) => [c.slug, c]));
  return CITY_ORDER.map((slug) => bySlug.get(slug)).filter(Boolean);
}

// Flatten published sujets across all rubriques. A sujet is considered
// published iff its rubrique exposes it in the `sujets` array (empty
// rubriques are "Bientôt" and never appear on the homepage).
function publishedSujets() {
  const out = [];
  for (const rubrique of connaissancesRubriques) {
    for (const sujet of rubrique.sujets) {
      out.push({ rubrique, sujet });
    }
  }
  return out;
}

export default function HomePage() {
  const { t, lp } = useT();
  const [cities, setCities] = useState([]);
  useEffect(() => {
    fetchCities()
      .then((data) => setCities(data || []))
      .catch(() => {});
  }, []);

  const orderedCities = orderCities(cities);
  const sujets = publishedSujets();

  const QUICK_LINKS = [
    { to: "/logement", icon: Home, title: t("nav.logement"), desc: t("links.logementDesc"), ready: true },
    { to: "/cout-de-la-vie", icon: Wallet, title: t("nav.coutDeLaVie"), desc: t("links.coutDesc"), ready: true },
    { to: "/demarches", icon: FileText, title: t("nav.demarches"), desc: t("links.demarchesDesc"), ready: true },
    { to: "/travailler-pologne", icon: Users, title: t("nav.travailler"), desc: t("links.travaillerDesc"), ready: true },
    { to: "/entrepreneuriat", icon: Briefcase, title: t("nav.entrepreneuriat"), desc: t("links.entrepreneuriatDesc"), ready: true },
    { to: "/sante", icon: HeartPulse, title: t("nav.sante"), desc: t("links.santeDesc"), ready: true },
    { to: "/education", icon: GraduationCap, title: t("nav.education"), desc: t("links.educationDesc"), ready: false },
  ];

  return (
    <div data-testid="home-page">
      <Hero />

      {/* 2. Piliers / quick-links */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-12">
          <span className="hej-chip" data-testid="quick-links-chip">
            <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
            {t("home.sectionsChip")}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mt-4">
            {t("home.sectionsTitle")}
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed">{t("home.sectionsSub")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {QUICK_LINKS.map((q) => {
            const Icon = q.icon;
            return (
              <Link
                to={lp(q.to)}
                key={q.to}
                className="hej-card p-6 group"
                data-testid={`quicklink-${q.to.slice(1)}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-[#0056D2]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#0056D2]" />
                  </div>
                  {!q.ready && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                      {t("home.comingSoonBadge")}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-semibold text-lg text-slate-900">{q.title}</h3>
                <p className="text-sm text-slate-600 mt-1.5">{q.desc}</p>
                <div className="mt-4 text-sm text-[#0056D2] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t("home.discover")} <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Promo retraite */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24"
        data-testid="home-retraite-promo"
      >
        <Link
          to={lp("/retraite-pologne")}
          className="hej-card p-8 md:p-10 block group grid grid-cols-1 md:grid-cols-3 gap-6 items-center"
          data-testid="home-retraite-card"
        >
          <div className="md:col-span-2">
            <h3 className="font-display font-semibold text-2xl sm:text-3xl text-slate-900 group-hover:text-[#0056D2] transition-colors">
              Prendre sa retraite en Pologne
            </h3>
            <p className="text-slate-600 leading-relaxed mt-3">
              Un coût de la vie nettement inférieur et une sécurité du quotidien parmi les meilleures d'Europe : la Pologne
              séduit de plus en plus de retraités francophones. L'essentiel se joue pourtant ailleurs, sur la fiscalité de votre
              pension, votre couverture santé et votre droit au séjour. Notre guide fait le point pour les ressortissants de
              France, de Belgique et de Suisse.
            </p>
          </div>
          <div className="md:justify-self-end">
            <span
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0056D2] group-hover:gap-2 transition-all"
              data-testid="home-retraite-cta"
            >
              Lire le guide <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </section>

      {/* 3. Où vivre en Pologne : carte + 7 fiches villes enrichies */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="home-cities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              {t("home.cities.title")}
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">{t("home.cities.intro")}</p>
          </div>

          <div className="mb-12">
            <PolandMap />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-cities-grid">
            {orderedCities.map((c) => (
              <CityLivingCard key={c.slug} city={c} />
            ))}
          </div>
        </div>
      </section>

      {/* 4. Convertisseur de devises, bande autonome */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
        data-testid="home-currency-section"
      >
        <CurrencyConverter />
      </section>

      {/* 5. Connaissances sur la Pologne (sujets publiés uniquement) */}
      <section
        className="bg-slate-50 py-16 md:py-24"
        data-testid="home-connaissances-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
                {t("home.connaissances.title")}
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                {t("home.connaissances.intro")}
              </p>
            </div>
            <Link
              to={lp("/connaissances")}
              className="text-[#0056D2] font-medium hover:underline"
              data-testid="home-connaissances-explore"
            >
              {t("home.connaissances.explore")} →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sujets.map(({ rubrique, sujet }) => (
              <Link
                key={`${rubrique.id}-${sujet.id}`}
                to={lp(`/connaissances/${rubrique.id}/${sujet.id}`)}
                className="hej-card p-6 group flex flex-col"
                data-testid={`home-sujet-${rubrique.id}-${sujet.id}`}
              >
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {rubrique.titre}
                </span>
                <h3 className="font-display font-semibold text-lg text-slate-900 mt-2">
                  {sujet.titreCourt || sujet.titre}
                </h3>
                {sujet.intro && (
                  <p className="text-sm text-slate-600 leading-relaxed mt-2 line-clamp-3">
                    {sujet.intro}
                  </p>
                )}
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#0056D2] group-hover:gap-2 transition-all">
                  {t("home.discover")} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Sur le blog (dynamique depuis BLOG_POSTS) */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
        data-testid="home-blog-section"
      >
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              {t("home.blog.title")}
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">{t("home.blog.intro")}</p>
          </div>
          <Link
            to={lp("/blog")}
            className="text-[#0056D2] font-medium hover:underline"
            data-testid="home-blog-see-all"
          >
            {t("home.blog.seeAll")} →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={lp(`/blog/${post.slug}`)}
              className="hej-card p-6 group flex flex-col"
              data-testid={`home-blog-${post.slug}`}
            >
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
                {post.dateLabel}
              </div>
              <h3 className="font-display font-semibold text-lg sm:text-xl text-slate-900 mt-3 group-hover:text-[#0056D2] transition">
                {post.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-2">{post.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#0056D2] group-hover:gap-2 transition-all">
                {t("home.blog.readMore")} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 7. CTA Newsletter (inchangé) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <div className="bg-gradient-to-br from-[#0056D2] to-[#0047AD] rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("home.ctaTitle")}
            </h2>
            <p className="text-blue-100 mt-3 mb-6 leading-relaxed">{t("home.ctaSub")}</p>
            <div className="bg-white rounded-2xl p-6">
              <NewsletterForm source="homepage_cta" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
